import React, { useState } from 'react';
import { Mail, RefreshCcw } from 'lucide-react';
import { FiLink } from 'react-icons/fi';
import toast from 'react-hot-toast';

import Integration from '../Integration';
import { API_URL } from '../../services/api';
import { DataState } from '../../context/DataProvider';
import { ENABLE_INTEGRATION_GMAIL } from '../../config/features';

const NoMailScreen = ({ isOutlook = true, noneAuthenticated }) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [showIntegration, setShowIntegration] = useState(false);
  const { userGoogleId } = DataState();
  // Gmail is off, so the provider's Gmail fallback (no Outlook yet) still means Outlook.
  const provider = ENABLE_INTEGRATION_GMAIL && !isOutlook ? 'Gmail' : 'Outlook';

  const handleSync = async () => {
    if (noneAuthenticated) {
      setShowIntegration(true);
      return;
    }
    setIsSyncing(true);
    try {
      const endpoint =
        provider === 'Outlook'
          ? `${API_URL}/outlook_mails/store`
          : `${API_URL}/gmail_emails/store`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${userGoogleId}` },
        credentials: 'include',
      });
      if (!response.ok) {
        const err = await response.json();
        throw err.detail || err.error || 'Sync failed';
      }
      const result = await response.json();
      // The sync runs in the background; mail appears as it is read.
      toast.success(result?.success || `${provider} sync started`);
    } catch (error) {
      toast.error(
        typeof error === 'string' ? error : 'Sync failed. Please try again.',
      );
    } finally {
      setIsSyncing(false);
    }
  };

  // Same look as the other right-panel tabs (Chats, Meetings): icon, one line, one action.
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-6 text-center">
      <Mail className="h-7 w-7 text-zinc-600" />
      <div className="space-y-1">
        <p className="text-[13px] font-medium text-zinc-300">
          {noneAuthenticated ? `${provider} is not connected` : 'No emails yet'}
        </p>
        <p className="max-w-[16rem] text-xs text-zinc-500">
          {noneAuthenticated
            ? `Connect ${provider} in Integrations to read, sort and answer your mail here.`
            : `Sync your ${provider} mailbox to bring your mail in.`}
        </p>
      </div>
      <button
        type="button"
        disabled={isSyncing}
        onClick={
          noneAuthenticated ? () => setShowIntegration(true) : handleSync
        }
        className="flex items-center gap-1.5 rounded-lg bg-[#5652CF] px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-[#5652CF]/80 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {noneAuthenticated ? (
          <FiLink className="h-3.5 w-3.5" />
        ) : (
          <RefreshCcw
            className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`}
          />
        )}
        {noneAuthenticated
          ? 'Open Integrations'
          : isSyncing
            ? 'Syncing...'
            : 'Sync email'}
      </button>
      {showIntegration && (
        // The full Integrations modal, as the header button opens it.
        <div className="fixed inset-0 z-50 text-left">
          <Integration
            onClose={() => setShowIntegration(false)}
            user_google_id={userGoogleId}
          />
        </div>
      )}
    </div>
  );
};

export default NoMailScreen;
